import { RefObject } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Color from '@arcgis/core/Color.js';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import LayerList from '@arcgis/core/widgets/LayerList';
import Fullscreen from '@arcgis/core/widgets/Fullscreen';
import ImageryLayer from '@arcgis/core/layers/ImageryLayer';
import { LAYERS_CONFIG } from '../config/layer.config';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

const { FOREST_PRESENCE, REFERENCE_DATA, GENUS_DISTRIBUTION, GENUS_RANGE } = LAYERS_CONFIG;

interface IPublishedLayer {
  url: string;
  expression: string;
  title: string;
  queryExtent?: boolean;
  id: string;
}

const SYMBOL_COLORS = {
  '95': 'rgba(54, 0, 90, 0.75)',    // very dark blue-purple
  '90': 'rgba(106, 13, 173, 0.75)', // medium blue-toned purple
  '85': 'rgba(186, 85, 211, 0.75)', // much lighter purple
} as any;

const getSymbolById = (id: string) => {
  return new SimpleFillSymbol({
    color: SYMBOL_COLORS[id], // Set the desired fill color with transparency
    outline: {
      // autocasts as new SimpleLineSymbol()
      color: SYMBOL_COLORS[id], // Set the desired fill color with transparency
      width: 1, // Set the desired outline width
    },
  }) as any;
};

const getRendererById = (id: string) => {
  return new SimpleRenderer({
    symbol: getSymbolById(id),
  });
};

class MapController {
  map: Map | null = null;
  mapView: MapView | null = null;

  initializeMap = async (domRef: RefObject<HTMLDivElement | null>, fullScreenDiv: RefObject<HTMLDivElement | null>) => {
    if (!domRef.current) return;

    this.map = new Map({
      basemap: 'topo',
    });

    this.mapView = new MapView({
      map: this.map,
      container: domRef.current,
      center: [20.80389, 47.75838],
      zoom: 3,

      highlightOptions: {
        color: Color.fromString('orange'),
      } as any,
    });

    this.mapView.when(() => {
      if (!this?.mapView) return;
      const layerList = new LayerList({
        view: this?.mapView,
      });

      this.mapView.ui.add(layerList, 'top-right');
      if (!fullScreenDiv.current) return;
      this.mapView.ui.add(
        new Fullscreen({
          view: this.mapView,
          element: fullScreenDiv.current,
        }),
        'top-left'
      );
    });
  };

  addSentineLayer = () => {
    const sentinelLayer = new ImageryLayer({
      ...FOREST_PRESENCE,
    });

    this.mapView?.map.add(sentinelLayer, this.mapView.map.layers.length - 1);
  };

  addReferenceDatLayer = (defExpression: string) => {
    const sentinelLayer = new FeatureLayer({
      ...REFERENCE_DATA,
      definitionExpression: `Analyzed = 'analyzed' ${defExpression}`,
    });

    this.mapView?.map.add(sentinelLayer);
  };

  addGenusLayer = (genusName: string) => {
    const genusDistributionLayer = new FeatureLayer({
      ...GENUS_DISTRIBUTION,
      url: GENUS_DISTRIBUTION.url(genusName),
    });
    this.mapView?.map.add(genusDistributionLayer, this.mapView.map.layers.length - 1);
  };

  addGenusRangeFeatureLayer = (genusName: string) => {
    const genusRangeFeatureLayer = new FeatureLayer({
      ...GENUS_RANGE,
      url: GENUS_RANGE.url(genusName),
    });
    this.mapView?.map.add(genusRangeFeatureLayer, this.mapView.map.layers.length - 1);
  };

  goTo = (featureSet: __esri.FeatureSet) => {
    this.mapView?.goTo({ target: featureSet.features }, { animate: true });
  };

  addLayer = (params: IPublishedLayer) => {
    const { url, expression, title, id } = params;
    const featureLayer = new FeatureLayer({
      url,
      definitionExpression: expression,
      renderer: getRendererById(id),
      id: title,
      title,
    });

    this.map?.add(featureLayer);
  };

  removeLayerById = (id: string) => {
    const layer = this.map?.findLayerById(id);
    if (layer) {
      this.map?.remove(layer);
    }
  };

  removeAllLayers = () => {
    this.mapView?.map.removeAll();
  };

  destroyMap = () => {
    if (this.mapView) {
      this.mapView.destroy();
    }
  };
}

export const mapController = new MapController();
